import React from "react";

import FormInput from "../../Inputs/FormInput";
import FormSelect from "../../Inputs/FormSelect";
import Button from "../../Button";

import { getActiveDimensionFields, UNIT_OPTIONS } from "./pageModal.utils";
import styles from "./pageModal.module.css";

const DIMENSION_FIELDS = [
  { name: "radius", label: "Radius" },
  { name: "length", label: "Length" },
  { name: "width", label: "Width" },
  { name: "height", label: "Height" },
];

const ProductManagementModalForm = ({
  formFields,
  formData,
  errors,
  readOnly,
  partCatalog,
  productPartRows,
  getCatalogEntry,
  handleProductPartChange,
  addProductPartRow,
  removeProductPartRow,
  getFieldLabel,
  normalizeSelectValue,
  handleChange,
  isEditMode,
}) => {
  const productFields = formFields.filter((field) => field.section === "Product Details");

  return (
    <>
      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Product Details</h3>
        <div className={styles.formGrid}>
          {productFields.map((field) => {
            if (field.type === "select") {
              return (
                <FormSelect
                  key={field.name}
                  label={getFieldLabel(field)}
                  name={field.name}
                  value={formData[field.name] || ""}
                  options={field.options || []}
                  error={errors[field.name]}
                  disabled={readOnly || field.disabled}
                  onChange={(e) =>
                    handleChange(field.name, normalizeSelectValue(field, e.target.value))
                  }
                />
              );
            }

            return (
              <FormInput
                key={field.name}
                label={getFieldLabel(field)}
                name={field.name}
                type={field.type}
                decimalPlaces={field.type === "number" ? 2 : undefined}
                error={errors[field.name]}
                accept={
                  field.type === "file" ? ".png,.jpg,.jpeg,application/pdf" : undefined
                }
                value={field.type !== "file" ? formData[field.name] || "" : undefined}
                disabled={readOnly || field.disabled}
                onChange={(e) => {
                  if (field.type === "file") {
                    handleChange(field.name, e.target.files[0]);
                  } else {
                    handleChange(field.name, e.target.value);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.partHeaderRow}>
          <h3 className={styles.sectionTitle}>Part Details</h3>
          {!readOnly && (
            <Button type="button" variant="primary" className="h-25" onClick={addProductPartRow}>
              <i className="bi bi-plus-lg"></i>
            </Button>
          )}
        </div>

        {errors.part_details && <div className={styles.partRowError}>{errors.part_details}</div>}

        {productPartRows.map((row, rowIndex) => {
          const catalogEntry = getCatalogEntry(row.part_management_fid);
          const activeDimensionFields = getActiveDimensionFields(row.part_type);
          const partTypeOptions = (catalogEntry?.specs || [])
            .map((spec) => spec.part_type)
            .filter(Boolean)
            .filter((value, idx, arr) => arr.indexOf(value) === idx)
            .map((value) => ({ label: value, value }));

          return (
            <div key={`part-row-${rowIndex}`} className={styles.partRowGrid}>
              <FormSelect
                label="Part Name"
                name={`part_name_${rowIndex}`}
                value={row.part_management_fid || ""}
                options={partCatalog.map((part) => ({
                  label: part.label,
                  value: part.value,
                }))}
                error={errors[`part_details_${rowIndex}_part_management_fid`]}
                disabled={readOnly || isEditMode}
                onChange={(e) =>
                  handleProductPartChange(rowIndex, "part_management_fid", e.target.value)
                }
              />

              <FormSelect
                label="Part Type"
                name={`part_type_${rowIndex}`}
                value={row.part_type || ""}
                options={partTypeOptions}
                disabled={readOnly || isEditMode || !row.part_management_fid}
                onChange={(e) => handleProductPartChange(rowIndex, "part_type", e.target.value)}
              />

              {DIMENSION_FIELDS.map((field) => (
                <FormInput
                  key={`${field.name}_${rowIndex}`}
                  label={field.label}
                  type="number"
                  decimalPlaces={2}
                  value={row[field.name] || ""}
                  disabled={
                    readOnly || !row.part_type || !activeDimensionFields.includes(field.name)
                  }
                  onChange={(e) =>
                    handleProductPartChange(rowIndex, field.name, e.target.value)
                  }
                />
              ))}
              <FormSelect
                label="Unit"
                name={`unit_${rowIndex}`}
                value={row.unit || ""}
                options={UNIT_OPTIONS}
                disabled={readOnly || !row.part_type}
                onChange={(e) => handleProductPartChange(rowIndex, "unit", e.target.value)}
              />
              <FormInput
                label="Volume"
                type="number"
                decimalPlaces={2}
                value={row.volume || ""}
                disabled
              />
              <FormInput
                label="Rate"
                type="number"
                decimalPlaces={2}
                value={row.rate || ""}
                disabled={readOnly}
                onChange={(e) => handleProductPartChange(rowIndex, "rate", e.target.value)}
              />

              {!readOnly && (
                <div className={styles.partDeleteCell}>
                  <Button
                    type="button"
                    variant="primary"
                    className="w-25 h-25"
                    onClick={() => removeProductPartRow(rowIndex)}
                  >
                    <i className="bi bi-dash-lg"></i>
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ProductManagementModalForm;
